/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Alchemy is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Alchemy is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Alchemy, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Alchemy:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Alchemy broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Alchemy utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Alchemy only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Alchemy.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Alchemy is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 *Alchemy provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* =================================================================================================
 * barcos-ec custom features
 * =================================================================================================
 * 読み込み方法: Theme Settings > Advanced > "HTML for <head> tag" に下記を1度だけ貼り付け
 *   <script src="{{ 'custom.js' | asset_url }}" defer></script>
 *
 * ここに追加する機能は、ページ判定 → 個別 init 関数の呼び出し、の構造で書く。
 * 各機能は他の機能と独立し、エラーが伝播しないようにする (try/catch で囲む)。
 * ================================================================================================= */

(function () {
	"use strict";

	// ---------------------------------------------------------------------------------------------
	// Feature: 再入荷通知ボタンの非表示制御 + バリアント切替時の再描画トリガー
	// ---------------------------------------------------------------------------------------------
	// バリアントの custom.no_restock メタフィールド (boolean) が true の時、
	// StoreCRM が自動注入するボタン ([data-type="restock-button"]) を CSS で非表示にする。
	//
	// 仕組み:
	//   1. <html data-no_restock="true"> 属性を JS でトグル
	//   2. インライン CSS が `[data-no_restock="true"] [data-type="restock-button"] { display:none }` で非表示化
	//   3. メタフィールドデータは Section Rendering API で取得
	//      (/products/{handle}?section_id=custom-product-data)
	//
	// 依存:
	//   - Splice <variant-picker> が発火する on:variant:change イベント
	//   - sections/custom-product-data.liquid (メタフィールド JSON を出力)
	// テーマはソフトナビ (ページ内容を差し替えて遷移) を使うため、<script> は遷移時に再実行されない。
	// そのため初期化は「フルロード時1回」ではなく、永続リスナー + URL 変化での再初期化で行う。
	function initNoRestock() {
		// 非表示用 CSS と初期 data-no_restock 属性は splice/layout/theme.liquid 側で
		// サーバーサイドに出力済み (CLS 回避のため)。ここではバリアント切替時の属性更新のみ担当。
		var noRestockSet = new Set();
		// 在庫切れバリアントID (Liquid の variant.available==false を custom-product-data 経由で取得)。
		// Splice の on:variant:change が返す variant.available は当ストアで信頼できないため使わない。
		var unavailableSet = new Set();
		var currentHandle = null; // 取得済み商品ハンドル (重複取得防止)

		function setHideState(hide) {
			// 逆転モデル: theme.liquid 側で [data-type="restock-button"] はデフォルト display:none。
			// 表示してよい時だけ html.no_restock-allow を付与する。
			// これにより新DOMが挿入された瞬間からデフォルト hide が効き、フラッシュが発生しない。
			if (hide) {
				document.documentElement.classList.remove("no_restock-allow");
			} else {
				document.documentElement.classList.add("no_restock-allow");
			}
		}

		// 表示許可の条件: non_no_restock かつ 在庫切れ (在庫判定は unavailableSet = Liquid の available==false)。
		//   - no_restock variant → 仕様で hide
		//   - 在庫あり variant   → hide (フラッシュ防止)
		//   - 在庫切れ + non_no_restock → allow を付けて StoreCRM の通常表示に任せる
		function shouldAllow(variant) {
			if (!variant || variant.id == null) return false;
			var id = String(variant.id);
			if (noRestockSet.has(id)) return false;
			return unavailableSet.has(id);
		}

		// StoreCRM に再入荷ボタンの再描画を促す (ロード時に描画ガードを使い切ると切替時に描画されないため)。
		// Splice の切替直後は StoreCRM の locationchange 処理と競合し取りこぼすので、遅延させ
		// ボタンが出るまでリトライする。世代番号で古い切替のリトライは中断する。
		var restockRerenderGen = 0;
		function forceStoreCRMRestockRerender() {
			var gen = ++restockRerenderGen;
			var attempts = 0;
			function nudge() {
				if (gen !== restockRerenderGen) return; // 新しい切替が来たら中断
				attempts += 1;
				try {
					var s = window.storecrm;
					if (s) s.lastRestockStateHref = ""; // URL 記憶をクリアして再評価を強制
					var g = window.__storecrmRestockRuntimeGuard;
					if (g) {
						g.attempts = 0;
						g.loading = false;
						g.loaded = false;
					}
					window.dispatchEvent(new Event("storecrm:locationchange"));
				} catch (err) {
					console.error("[custom.js] no_restock: StoreCRM re-render nudge failed", err);
				}
				if (attempts < 6 && !document.querySelector('[data-type="restock-button"]')) {
					setTimeout(nudge, 350);
				}
			}
			setTimeout(nudge, 250);
		}

		// 商品データ (no_restock / 在庫切れ バリアントID) を取得し、現バリアントで再評価する。
		function loadProductData(handle) {
			noRestockSet.clear();
			unavailableSet.clear();
			fetch("/products/" + handle + "?section_id=custom-product-data", {
				headers: { Accept: "text/html" },
				cache: "no-store", // 在庫状態は鮮度重視。古いセクション応答のキャッシュを避ける
			})
				.then(function (res) {
					if (!res.ok) throw new Error("HTTP " + res.status);
					return res.text();
				})
				.then(function (html) {
					var doc = new DOMParser().parseFromString(html, "text/html");
					var dataEl = doc.getElementById("custom-product-data-payload");
					if (!dataEl) return;
					var data = JSON.parse(dataEl.textContent || "{}");
					(data.no_restock_variant_ids || []).forEach(function (id) {
						noRestockSet.add(String(id));
					});
					(data.unavailable_variant_ids || []).forEach(function (id) {
						unavailableSet.add(String(id));
					});
					// 現在の選択バリアントで初期表示を確定する。
					var vid = new URL(window.location.href).searchParams.get("variant");
					if (vid && unavailableSet.has(String(vid)) && !noRestockSet.has(String(vid))) {
						setHideState(false);
						forceStoreCRMRestockRerender();
					} else {
						setHideState(true);
					}
				})
				.catch(function (err) {
					console.error("[custom.js] no_restock: failed to fetch product data", err);
				});
		}

		// 現在の URL が商品ページなら (別商品に変わっていれば) データを読み直す。
		function handlePage() {
			var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
			if (!m) {
				currentHandle = null;
				return;
			}
			if (m[1] === currentHandle) return; // 同一商品は再取得しない
			currentHandle = m[1];
			loadProductData(m[1]);
		}

		// --- 永続リスナー (ソフトナビでも JS context が残るため一度だけ登録) ---

		// バリアント切替を購読。
		document.addEventListener("on:variant:change", function (event) {
			var variant = event.detail && event.detail.variant;
			var allow = shouldAllow(variant);
			setHideState(!allow);
			if (allow) {
				forceStoreCRMRestockRerender(); // 在庫切れ + non_no_restock: 再描画を促す
			} else {
				restockRerenderGen += 1; // 在庫あり等へ切替: 進行中のリトライを中断
			}
		});

		// バリアントピッカー操作時に「表示許可」を即取り下げる (= 非表示化・フラッシュ防止)。
		// document 委譲で <variant-picker> が DOM 置換されてもリスナーが残るようにする。
		document.addEventListener(
			"change",
			function (e) {
				if (e.target && e.target.closest && e.target.closest("variant-picker")) {
					setHideState(true);
				}
			},
			true,
		);

		// ソフトナビ検知: history を自前でラップして URL 変化イベントを発火 + popstate も購読。
		["pushState", "replaceState"].forEach(function (fn) {
			var orig = history[fn];
			if (orig.__noRestockWrapped) return;
			var wrapped = function () {
				var r = orig.apply(this, arguments);
				window.dispatchEvent(new Event("custom:navigation"));
				return r;
			};
			wrapped.__noRestockWrapped = true;
			history[fn] = wrapped;
		});
		window.addEventListener("custom:navigation", handlePage);
		window.addEventListener("popstate", handlePage);

		handlePage(); // 初回 (フルロードで商品ページに直接来た場合)
	}

	// ---------------------------------------------------------------------------------------------
	// Feature: variant 切替時の画面全体フラッシュ抑制
	// ---------------------------------------------------------------------------------------------
	// Splice の <variant-picker> は variant 切替の度に Section Rendering API でセクション全体を
	// 再フェッチし、data-dynamic-variant-content が付いた要素を全て DOM 置換する。
	// description (1000px超) などの大きな要素が毎回再描画されるため画面全体がフラッシュする。
	//
	// 対策: variant に依存しない内容のブロックから data-dynamic-variant-content 属性を剥がす。
	// Splice 側は querySelectorAll('[data-dynamic-variant-content]') で対象を取得するため、
	// 属性が無ければ置換リストに入らない。
	//
	// 対象: description / title / vendor-text。これらは product.* 由来で variant では変化しない。
	// -modals は商品問い合わせ用のポップアップ(固定ページ内容)で variant 非依存。
	// 残してしまうと DOM 置換時に <modal-dialog> がヘッダー(z-index:100)の下に潜り込む。
	// (price / variant-picker / buy-buttons 等は variant 依存なので残す)
	// 注意: pickup-availability を有効にしている場合は -modals を strip しないこと(在庫表示が更新されない)。
	function stripUnnecessaryDynamicUpdates() {
		var stripSuffixes = ["-description", "-title", "-vendor-text", "-modals"];
		document
			.querySelectorAll("[data-dynamic-variant-content]")
			.forEach(function (el) {
				var key = el.dataset.dynamicVariantContent;
				if (!key) return;
				if (
					stripSuffixes.some(function (s) {
						return key.endsWith(s);
					})
				) {
					el.removeAttribute("data-dynamic-variant-content");
				}
			});

		// custom-product-inquiry-modal を含むブロックも variant 切替で再描画させない。
		// 再描画すると <modal-dialog> が同IDで二重に存在し、open 時に document order 先頭の
		// ネストされた新規モーダルが選ばれてヘッダーの下に潜る。
		// hidden variant input の更新はスニペット側の on:variant:change リスナーで行うため、
		// ブロック自体の再描画は不要。
		document
			.querySelectorAll("append-modals [data-dynamic-variant-content]")
			.forEach(function (el) {
				if (
					el.querySelector(
						'.custom-inquiry-opener, [id^="custom-inquiry-modal-"]',
					)
				) {
					el.removeAttribute("data-dynamic-variant-content");
				}
			});
	}

	// ---------------------------------------------------------------------------------------------
	// Feature: お気に入りページ (/apps/<handle>/favorites) ローダー解除タイミング制御
	// ---------------------------------------------------------------------------------------------
	// theme.liquid <head> の inline script が以下を実行済:
	//   - <html> に `is-favorites-page` + `is-favorites-loading` を付与
	//   - window.__favLoadStart に開始時刻を記録
	// このタイミングで CSS は loader を表示している。
	//
	// ここでは「本当に中身が出揃った」タイミングを検知して loading class を外す:
	//   - .storecrm_customlist (または _favoritelist) 内に table の tr が出現
	//     OR
	//   - 空メッセージ #non_item_text が display:none ではない (= 空状態として可視化)
	// が成立した時点で「中身 ready」と判定。
	//
	// 制約:
	//   - 最低 500ms は loader を視認させる (一瞬で消えると目がチカチカするため)
	//   - 5 秒で強制解除 (永続表示防止のセーフティ)
	function initFavoritesLoader() {
		var html = document.documentElement;
		if (!html.classList.contains("is-favorites-loading")) return;

		var MIN_VISIBLE_MS = 500;
		var MAX_WAIT_MS = 5000;
		var startTime = window.__favLoadStart || Date.now();
		var removed = false;

		function done() {
			if (removed) return;
			removed = true;
			html.classList.remove("is-favorites-loading");
		}

		function isReady() {
			var list = document.querySelector(
				".storecrm_customlist, .storecrm_favoritelist",
			);
			if (!list) return false;
			// 1) tr が居れば items 描画済
			if (list.querySelector("tbody tr")) return true;
			// 2) 空メッセージが display:block 系で生きていれば「お気に入り無し」状態として ready
			var empty = list.querySelector(
				"#non_item_text, .storecrm_customlist_non_item",
			);
			if (empty && empty.offsetParent !== null) {
				var style = window.getComputedStyle(empty);
				if (style.display !== "none") return true;
			}
			return false;
		}

		function tryFinish() {
			if (!isReady()) return false;
			var elapsed = Date.now() - startTime;
			if (elapsed < MIN_VISIBLE_MS) {
				setTimeout(done, MIN_VISIBLE_MS - elapsed);
			} else {
				done();
			}
			return true;
		}

		// 既に ready なら即トライ (server-side 完全描画の場合)
		if (tryFinish()) return;

		// まだなら DOM 変更を監視
		var observer = new MutationObserver(function () {
			if (tryFinish()) observer.disconnect();
		});
		observer.observe(document.body, { childList: true, subtree: true });

		// セーフティ: 5 秒で強制解除
		setTimeout(function () {
			observer.disconnect();
			done();
		}, MAX_WAIT_MS);
	}

	// ---------------------------------------------------------------------------------------------
	// Feature: お気に入り一覧の削除ボタン押下で行を即時非表示 (StoreCRM の再描画完了を待たない)
	// ---------------------------------------------------------------------------------------------
	function initFavoritesInstantRemove() {
		var pendingIds = new Set();
		var style = null;
		var observer = null;

		function syncStyle() {
			style.textContent = Array.from(pendingIds)
				.map(function (id) {
					return ".storecrm_customlist_id-" + id + "{display:none!important}";
				})
				.join("");
		}

		// StoreCRM の再描画で行が消えた ID から隠しルールを外す
		function releaseRendered() {
			pendingIds.forEach(function (id) {
				if (!document.querySelector(".storecrm_customlist_id-" + id)) {
					pendingIds.delete(id);
				}
			});
			syncStyle();
			if (!pendingIds.size) {
				observer.disconnect();
				observer = null;
			}
		}

		document.addEventListener(
			"click",
			function (e) {
				var link = e.target.closest(
					".storecrm_customlist a[data-product-id]:not([data-cart-product-id])",
				);
				if (!link || !link.dataset.productId) return;
				var row = link.closest("tr");
				if (!row) return;

				row.style.display = "none";
				var variantId = link.dataset.variantId;
				if (variantId) {
					if (!style) {
						style = document.createElement("style");
						document.head.appendChild(style);
					}
					pendingIds.add(variantId);
					syncStyle();
					if (!observer) {
						observer = new MutationObserver(releaseRendered);
						observer.observe(document.body, { childList: true, subtree: true });
					}
				}

				var list = row.closest(".storecrm_customlist");
				var hasVisible = Array.prototype.some.call(
					list.querySelectorAll("tbody tr"),
					function (tr) {
						return tr.offsetParent !== null;
					},
				);
				if (!hasVisible) {
					var table = list.querySelector("table");
					var empty = list.querySelector(".storecrm_customlist_non_item");
					if (table) table.style.display = "none";
					if (empty) empty.style.display = "";
				}
			},
			true,
		);
	}

	// ---------------------------------------------------------------------------------------------
	// Feature: 商品カードのお気に入りボタン (登録は StoreCRM に委譲し、状態はバリアント単位で表示)
	// ---------------------------------------------------------------------------------------------
	function initCardFavorite() {
		if (customElements.get("custom-card-favorite")) return;

		var PENDING_TIMEOUT_MS = 10000;
		var HOOK_TRIES_AFTER_LOAD = 150;
		var hooked = false;
		var hookTimer = null;

		function favoritedVariantIds() {
			try {
				var data = JSON.parse(localStorage.getItem("storecrm_wishlist") || "{}");
				var variants = Array.isArray(data.variants) ? data.variants : [];
				return variants.map(function (id) {
					return String(id).replace(/^.*\//, "");
				});
			} catch (e) {
				return [];
			}
		}

		// 通信中のカードは楽観表示を保つため同期対象から外す
		function syncAll() {
			var ids = favoritedVariantIds();
			document.querySelectorAll("custom-card-favorite:not(.is-pending)").forEach(function (el) {
				el.sync(ids);
			});
		}

		// 元メソッドの完了後（失敗時も）に第 1 引数を渡して callback を呼ぶ
		function runAfter(obj, name, callback) {
			var original = obj[name];
			obj[name] = function () {
				var target = arguments[0];
				var result = original.apply(this, arguments);
				var done = function () {
					callback(target);
				};
				Promise.resolve(result).then(done, done);
				return result;
			};
		}

		// 描画（遅延再描画含む）・ログイン後の同期・登録/解除の完了時にカードの状態を更新
		function hookStorecrm() {
			var sc = window.storecrm;
			var ready =
				sc &&
				["doRender", "syncWishlist", "doToggleWishlist"].every(function (name) {
					return typeof sc[name] === "function";
				});
			if (!ready) return false;
			runAfter(sc, "doRender", syncAll);
			runAfter(sc, "syncWishlist", syncAll);
			runAfter(sc, "doToggleWishlist", function (el) {
				if (el && el.localName === "custom-card-favorite") el.sync();
				syncAll();
			});
			syncAll();
			return true;
		}

		// StoreCRM は window load 後に非同期で読み込まれるため load 完了から 30 秒まで待つ
		function ensureHook() {
			if (hooked || hookTimer) return;
			if ((hooked = hookStorecrm())) return;
			var tries = 0;
			hookTimer = setInterval(function () {
				var loaded = document.readyState === "complete";
				if ((hooked = hookStorecrm()) || (loaded && ++tries > HOOK_TRIES_AFTER_LOAD)) {
					clearInterval(hookTimer);
					hookTimer = null;
				}
			}, 200);
		}

		class CustomCardFavorite extends HTMLElement {
			connectedCallback() {
				if (!this.button) {
					this.button = this.querySelector("button");
					this.button.addEventListener("click", this.handleClick.bind(this));
					var card = this.closest("product-card");
					if (card) card.addEventListener("change", this.handleSwatchChange.bind(this));
				}
				this.sync();
				ensureHook();
			}

			sync(ids) {
				ids = ids || favoritedVariantIds();
				clearTimeout(this.pendingTimer);
				this.classList.remove("is-pending");
				this.setFavorited(ids.indexOf(String(this.dataset.variantId)) !== -1);
			}

			setFavorited(favorited) {
				this.classList.toggle("is-favorited", favorited);
				this.button.setAttribute("aria-pressed", favorited ? "true" : "false");
			}

			// カードのスウォッチ選択に登録対象バリアントを追従させる
			handleSwatchChange(evt) {
				var variantId = evt.target.matches(".js-option") && evt.target.dataset.variantId;
				if (!variantId) return;
				this.dataset.variantId = variantId;
				this.sync();
			}

			handleClick(evt) {
				evt.preventDefault();
				var sc = window.storecrm;
				if (!sc || !this.dataset.variantId || this.classList.contains("is-pending")) return;

				var favorited = this.classList.contains("is-favorited");
				var config = (sc.config && sc.config.favorite) || {};
				this.setFavorited(!favorited);
				this.classList.add("is-pending");
				this.pendingTimer = setTimeout(this.sync.bind(this), PENDING_TIMEOUT_MS);
				sc.toggleWishlist(this, favorited, config.is_only_member, config.non_member_link);
			}
		}

		customElements.define("custom-card-favorite", CustomCardFavorite);
	}

	// ---------------------------------------------------------------------------------------------
	// Feature: カートドロワーのおすすめ商品の二重取得を抑止
	// ---------------------------------------------------------------------------------------------
	// カート追加時 CartDrawer.renderContents() がドロワーを innerHTML で丸ごと差し替えるため、
	// 読み込み済みの <product-recommendations> が空要素に戻り再フェッチが走る (二度読み込み・チラつき)。
	// おすすめ対象商品 (data-product-id) が変わっていなければ、旧要素をそのまま新 DOM に戻す。
	function initKeepCartRecommendations() {
		customElements.whenDefined("cart-drawer").then(function () {
			var CartDrawer = customElements.get("cart-drawer");
			if (!CartDrawer || CartDrawer.prototype.renderContents.__keepRecs) return;

			var original = CartDrawer.prototype.renderContents;
			var wrapped = function () {
				var self = this;
				var args = arguments;
				var previous = document.getElementById("cart-recommendations");
				if (previous && !previous.children.length) previous = null;

				return Promise.resolve(original.apply(self, args)).then(function (result) {
					// IntersectionObserver の発火はフレーム境界なので、ここで戻せば再フェッチごと差し替わる
					var current = document.getElementById("cart-recommendations");
					if (
						previous &&
						current &&
						current !== previous &&
						!current.children.length &&
						current.dataset.productId === previous.dataset.productId
					) {
						current.replaceWith(previous);
					}
					return result;
				});
			};
			wrapped.__keepRecs = true;
			CartDrawer.prototype.renderContents = wrapped;
		});
	}

	// ---------------------------------------------------------------------------------------------
	// Feature: ラッピングの単独購入防止 + 対象商品との削除連動 + 数量変更不可
	// ---------------------------------------------------------------------------------------------
	// 商品行の properties["ラッピング"]="あり" と、ラッピング行の _wrapping_for (対象 variant id) で紐付く (custom-product-option-wrapping.js)。
	function initCartWrappingGuard() {
		var WRAPPING_FOR = "_wrapping_for";
		var WRAPPED_KEY = "ラッピング";
		var WRAPPED_VALUE = "あり";
		var CACHE_KEY = "custom-wrapping-variant-ids";
		var ONLY_WRAPPING_CLASS = "is-cart-wrapping-only";
		var ONLY_WRAPPING_MESSAGE =
			"ラッピングのみではご購入いただけません。ラッピングする商品をカートに追加してください。";
		var wrappingIds = null;
		var loading = null;

		function readCachedIds() {
			try {
				var cached = JSON.parse(window.sessionStorage.getItem(CACHE_KEY));
				return Array.isArray(cached) ? cached : null;
			} catch (e) {
				return null;
			}
		}

		function setWrappingIds(ids) {
			wrappingIds = new Set(
				ids.map(String).filter(function (id) {
					return /^\d+$/.test(id);
				})
			);
			injectQtyLockStyle();
			return wrappingIds;
		}

		// ラッピング商品のバリアントIDを取得する (失敗時は空扱いにして購入を妨げない)
		function loadWrappingIds() {
			if (wrappingIds) return Promise.resolve(wrappingIds);
			if (!loading) {
				loading = fetch("/?section_id=custom-wrapping-data")
					.then(function (res) {
						if (!res.ok) throw new Error("HTTP " + res.status);
						return res.text();
					})
					.then(function (html) {
						var doc = new DOMParser().parseFromString(html, "text/html");
						var payload = doc.getElementById("custom-wrapping-data-payload");
						var ids = payload ? (JSON.parse(payload.textContent).variant_ids || []) : [];
						try {
							window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(ids));
						} catch (e) {
							/* 保存できない環境では毎回取得する */
						}
						return ids;
					})
					.catch(function (err) {
						console.error("[wrapping-guard] failed to load wrapping data", err);
						return [];
					})
					.then(setWrappingIds);
			}
			return loading;
		}

		function isWrapping(variantId) {
			return wrappingIds.has(String(variantId));
		}

		function isOnlyWrapping(variantIds) {
			return variantIds.length > 0 && variantIds.every(isWrapping);
		}

		function getRowVariantIds(root) {
			return Array.prototype.map.call(root.querySelectorAll(".cart-item[data-variant-id]"), function (row) {
				return row.dataset.variantId;
			});
		}

		function toggleOnlyWrappingClass(variantIds) {
			document.documentElement.classList.toggle(ONLY_WRAPPING_CLASS, isOnlyWrapping(variantIds));
		}

		// ラッピング行の数量ステッパーを隠し、数量を文字として表示する (行は再描画されるためスタイルで当てる)
		function injectQtyLockStyle() {
			if (!wrappingIds.size || document.getElementById("custom-wrapping-qty-lock")) return;
			var rows = Array.from(wrappingIds).map(function (id) {
				return '.cart-item[data-variant-id="' + id + '"]';
			});
			var select = function (suffix) {
				return rows
					.map(function (row) {
						return row + " " + suffix;
					})
					.join(",");
			};
			var style = document.createElement("style");
			style.id = "custom-wrapping-qty-lock";
			style.textContent =
				select(".qty-input__btn") + "{display:none}" +
				select(".qty-input") + "," + select(".qty-input__input") +
				"{pointer-events:none;border-color:transparent;background:transparent;-moz-appearance:textfield}" +
				select(".qty-input__input::-webkit-inner-spin-button") + "," +
				select(".qty-input__input::-webkit-outer-spin-button") + "{-webkit-appearance:none;margin:0}";
			document.head.appendChild(style);
		}

		// 解消が必要な行を1件返す: 対象商品の無いラッピング行 / ラッピング行の無い「ラッピング: あり」の商品行
		function findCartFix(items) {
			var hasOthers = false;
			var wrappedIds = {};
			var linkedIds = {};
			items.forEach(function (item) {
				var props = item.properties || {};
				if (isWrapping(item.variant_id)) {
					if (props[WRAPPING_FOR]) linkedIds[props[WRAPPING_FOR]] = true;
					return;
				}
				hasOthers = true;
				if (props[WRAPPED_KEY] === WRAPPED_VALUE) wrappedIds[item.variant_id] = true;
			});

			for (var i = 0; i < items.length; i++) {
				if (!isWrapping(items[i].variant_id)) continue;
				var parentId = (items[i].properties || {})[WRAPPING_FOR];
				if (!hasOthers || (parentId && !wrappedIds[parentId])) return { type: "remove", line: i + 1 };
			}
			for (var j = 0; j < items.length; j++) {
				var item = items[j];
				if (isWrapping(item.variant_id) || (item.properties || {})[WRAPPED_KEY] !== WRAPPED_VALUE) continue;
				if (!linkedIds[item.variant_id]) return { type: "unwrap", line: j + 1, item: item };
			}
			return null;
		}

		function postJson(url, body) {
			return fetch(url, {
				method: "POST",
				headers: { "Content-Type": "application/json", Accept: "application/json" },
				body: JSON.stringify(body)
			}).then(function (res) {
				if (!res.ok) throw new Error("HTTP " + res.status);
				return res.json();
			});
		}

		// Shopify は空のプロパティへの変更を無視するため、行を削除してプロパティ無しで追加し直す
		function readdLine(item, sectionNames) {
			return postJson(theme.routes.cartChange, { id: item.key, quantity: 0 })
				.then(function () {
					return postJson(theme.routes.cartAdd, {
						items: [{ id: item.variant_id, quantity: item.quantity }],
						sections: sectionNames,
						sections_url: window.location.pathname
					}).catch(function (err) {
						// 削除だけ反映された状態を画面に残さない
						window.location.reload();
						throw err;
					});
				})
				.then(function (added) {
					return fetch(theme.routes.cart + ".js", { headers: { Accept: "application/json" } })
						.then(function (res) {
							return res.json();
						})
						.then(function (cart) {
							cart.sections = added.sections;
							return cart;
						});
				});
		}

		// 商品行から「ラッピング: あり」を外し、<cart-items> と同じ手順で再描画する
		function unwrapLine(cartItems, fix) {
			var properties = Object.assign({}, fix.item.properties);
			delete properties[WRAPPED_KEY];
			var sections = cartItems.getSectionsToRender();
			var sectionNames = sections.map(function (section) {
				return section.section;
			});
			cartItems.classList.add("pointer-events-none");

			var request = Object.keys(properties).length
				? postJson(theme.routes.cartChange, {
						line: fix.line,
						quantity: fix.item.quantity,
						properties: properties,
						sections: sectionNames,
						sections_url: window.location.pathname
					})
				: readdLine(fix.item, sectionNames);

			request
				.then(function (data) {
					sections.forEach(function (section) {
						var sectionEl = document.getElementById(section.id);
						if (!sectionEl) return;
						var el = sectionEl.querySelector(section.selector) || sectionEl;
						el.innerHTML = cartItems.constructor.getElementHTML(data.sections[section.section], section.selector);
					});
					cartItems.dispatchEvent(new CustomEvent("on:line-item:change", { bubbles: true, detail: { cart: data } }));
				})
				.catch(function (err) {
					console.error("[wrapping-guard] failed to unwrap line", err);
				})
				.finally(function () {
					cartItems.classList.remove("pointer-events-none");
				});
		}

		// 数量変更・削除後にラッピングと商品行を揃える (1行ずつ直し、再発火するイベントで残りを処理)
		var lastUnwrapKey = null;
		document.addEventListener("on:line-item:change", function (evt) {
			var cart = evt.detail && evt.detail.cart;
			var cartItems = evt.target;
			if (!cart || !Array.isArray(cart.items) || typeof cartItems.updateQuantity !== "function") return;

			loadWrappingIds().then(function () {
				toggleOnlyWrappingClass(cart.items.map(function (item) {
					return item.variant_id;
				}));
				var fix = findCartFix(cart.items);
				// 反映されなかった行を繰り返し更新しない
				var unwrapKey = fix && fix.type === "unwrap" ? fix.item.key : null;
				if (unwrapKey && unwrapKey === lastUnwrapKey) return;
				lastUnwrapKey = unwrapKey;
				if (!fix) return;
				if (fix.type === "remove") cartItems.updateQuantity(fix.line, 0);
				else unwrapLine(cartItems, fix);
			});
		});

		// ラッピング行の数量変更は <cart-items> に届く前に打ち消す (キーボード入力対策)
		window.addEventListener(
			"change",
			function (evt) {
				var input = evt.target;
				if (!(input instanceof HTMLInputElement) || input.name !== "updates[]") return;
				var row = input.closest(".cart-item[data-variant-id]");
				if (!row || (wrappingIds && !isWrapping(row.dataset.variantId))) return;

				evt.stopImmediatePropagation();
				loadWrappingIds().then(function () {
					if (isWrapping(row.dataset.variantId)) {
						input.value = input.dataset.initialValue;
					} else {
						input.dispatchEvent(new Event("change", { bubbles: true }));
					}
				});
			},
			true
		);

		// ラッピングのみのカートはチェックアウト送信を止める (外部アプリより先に window capture で判定)
		window.addEventListener(
			"submit",
			function (evt) {
				var form = evt.target;
				if (!(form instanceof HTMLFormElement) || !form.querySelector("cart-items")) return;
				if (wrappingIds && !isOnlyWrapping(getRowVariantIds(form))) return;

				evt.preventDefault();
				evt.stopImmediatePropagation();
				var submitter = evt.submitter;
				loadWrappingIds().then(function () {
					if (isOnlyWrapping(getRowVariantIds(form))) {
						window.alert(ONLY_WRAPPING_MESSAGE);
					} else {
						form.requestSubmit(submitter);
					}
				});
			},
			true
		);

		var cachedIds = readCachedIds();
		if (cachedIds) {
			setWrappingIds(cachedIds);
		} else if (document.querySelector('cart-items[data-empty="false"]')) {
			loadWrappingIds();
		}

		// カートページは Shop Pay 等の追加ボタンを隠すため読み込み時にも判定する
		var cartForm = document.getElementById("cart");
		if (cartForm && cartForm.querySelector('cart-items[data-empty="false"]')) {
			loadWrappingIds().then(function () {
				toggleOnlyWrappingClass(getRowVariantIds(cartForm));
			});
		}
	}

	// ---------------------------------------------------------------------------------------------
	// Page dispatch
	// ---------------------------------------------------------------------------------------------
	try {
		// initNoRestock は内部でソフトナビを購読し商品ページを自己判定するため常に実行する。
		initNoRestock();
		if (window.location.pathname.indexOf("/products/") === 0) {
			stripUnnecessaryDynamicUpdates();
		}
		initFavoritesLoader();
		initFavoritesInstantRemove();
		initCardFavorite();
		initKeepCartRecommendations();
		initCartWrappingGuard();
	} catch (err) {
		console.error("[custom.js] init error", err);
	}
})();

/* PC: アクティブ親のセクション直後へ children-area を移動し「該当セクションの右隣」に表示 */
function ccDyncatPositionPanel(mega) {
	if (!mega) return;
	var parents = mega.querySelector(".dyncat-mega__parents");
	var childrenArea = mega.querySelector(".dyncat-mega__children-area");
	if (!parents || !childrenArea) return;
	var activeItem = parents.querySelector(
		".dyncat-mega__parent-item.is-active",
	);
	var section = activeItem
		? activeItem.closest(".dyncat-mega__section")
		: parents.querySelector(".dyncat-mega__section");
	if (!section) return;
	if (section.nextElementSibling !== childrenArea) {
		section.insertAdjacentElement("afterend", childrenArea);
	}
}

/* ============================================================
   動的2階層カテゴリメニュー (cc-dynamic-category-menu)
   - デスクトップ: 親クリックで右ペイン(.dyncat-mega__panel)切替
   - モバイル:   既存テーマの MenuDrawer に乗るため独自JSなし

   既存メガメニューは #mega-menu-tier-2 に毎回 cloneNode するため、
   イベントは document に委譲する必要がある。
   ============================================================ */
(function () {
	document.addEventListener("click", function (e) {
		var link = e.target.closest(".dyncat-mega__parent-link");
		if (!link) return;

		/* 子なし親には data-dyncat-target が出力されないので、通常のリンクとして
       ページ遷移させる（preventDefault しない） */
		var handle = link.getAttribute("data-dyncat-target");
		if (!handle) return;

		e.preventDefault();
		e.stopPropagation();

		var mega = link.closest(".dyncat-mega");
		if (!mega) return;

		/* 親 active 切替 */
		mega
			.querySelectorAll(".dyncat-mega__parent-item")
			.forEach(function (item) {
				var isMatch = item.contains(link);
				item.classList.toggle("is-active", isMatch);
				var l = item.querySelector(".dyncat-mega__parent-link");
				if (l) l.setAttribute("aria-selected", isMatch ? "true" : "false");
			});

		/* 右ペイン切替（フェードイン: hidden 解除 → 次フレームで is-active 付与） */
		mega.querySelectorAll(".dyncat-mega__panel").forEach(function (panel) {
			var match = panel.getAttribute("data-dyncat-panel") === handle;
			if (match) {
				panel.removeAttribute("hidden");
				requestAnimationFrame(function () {
					panel.classList.add("is-active");
				});
			} else {
				panel.classList.remove("is-active");
				panel.setAttribute("hidden", "");
			}
		});

		/* パネルをアクティブ親のセクション右隣へ */
		ccDyncatPositionPanel(mega);
	});
})();

/* ============================================================
   All Products メガメニューを sticky 化（hover の自動クローズを無効化）
   - hover/クリックで開くのは従来どおり。開いた後は hover が外れても
     閉じず、次のいずれかでのみ閉じる: メニュー外クリック / Escape /
     All Products 再クリック / 他のナビ項目を開く（テーマの reset）。
   - テーマは #mega-menu-tier-2 と各ナビ li の mouseleave で閉じるが、
     再フロー(画像遅延読込・カルーセル自動再生)や 20px の隙間で
     マウスが乗っていても閉じてしまうため、その自動クローズを止める。
   - main.js は変更せず、capture 段階で mouseleave を打ち消す。
   ============================================================ */
(function () {
	var tier2 = document.getElementById("mega-menu-tier-2");
	if (!tier2) return;

	/* tier-2 の中身が All Products メガで、かつ表示中か */
	function isOpen() {
		return (
			tier2.classList.contains("tier-appeared") &&
			!!tier2.querySelector(".dyncat-mega")
		);
	}
	/* テーマ closeChildMenu と同等の明示クローズ */
	function forceClose() {
		document
			.querySelectorAll('.dyncat-li > a.has-children[aria-expanded="true"]')
			.forEach(function (a) {
				a.setAttribute("aria-expanded", "false");
			});
		tier2.classList.remove("tier-appeared");
	}
	function openMenu() {
		var li = document.querySelector(".dyncat-li");
		if (li) li.dispatchEvent(new Event("mouseenter"));
	}

	/* クリックで開閉（既定のリンク遷移は抑止）+ メニュー外クリックで閉じる */
	document.addEventListener("click", function (e) {
		var link = e.target.closest(".dyncat-li > a.has-children");
		if (link) {
			e.preventDefault();
			if (isOpen()) forceClose();
			else openMenu();
			return;
		}
		if (
			isOpen() &&
			!e.target.closest("#mega-menu-tier-2") &&
			!e.target.closest(".dyncat-li")
		) {
			forceClose();
		}
	});

	/* Escape で閉じる */
	document.addEventListener("keydown", function (e) {
		if (e.key === "Escape" && isOpen()) forceClose();
	});

	/* All Products メガが開いている間は hover 由来の自動クローズを打ち消す。
	   他ナビ項目を開くと openChildMenu が reset するので switch は従来どおり。 */
	document.addEventListener(
		"mouseleave",
		function (e) {
			var t = e.target;
			if (!t || !t.classList || !isOpen()) return;
			if (t.id === "mega-menu-tier-2" || t.classList.contains("dyncat-li")) {
				e.stopImmediatePropagation();
			}
		},
		true,
	);

	/* CATEGORY 以外のナビ項目を hover したら閉じる */
	document.addEventListener("mouseover", function (e) {
		var t = e.target;
		if (!t || !t.closest || !isOpen()) return;
		if (t.closest(".dyncat-li") || t.closest("#mega-menu-tier-2")) return;
		if (t.closest(".cc-header-scrolled-nav__item, .tier-1 li")) forceClose();
	});
})();

/* ============================================================
   PC: children-area の最小サイズを「最大パネル」に合わせて固定
   - 親カテゴリ切替でサブパネルの寸法が変わりメガメニューがジャンプするのを防ぐ。
   - 全パネルを同期的に一時表示して offsetWidth/Height を計測（途中で再描画は
     入らないため画面のちらつきなし）、最大値を min-width/min-height に設定。
   - clone 元(.dyncat-li 配下)にも適用し以降の clone に継承させる。
   ============================================================ */
(function () {
	var tier2 = document.getElementById("mega-menu-tier-2");
	if (!tier2 || !("MutationObserver" in window)) return;
	var cache = null;

	function measureMax(area) {
		var panels = Array.prototype.slice.call(
			area.querySelectorAll(".dyncat-mega__panel"),
		);
		if (!panels.length) return null;
		/* 実際のレンダリング幅を基準に高さを計測（幅で折り返し量＝高さが変わるため） */
		var width = Math.round(area.getBoundingClientRect().width);
		var maxW = 0;
		var maxH = 0;
		panels.forEach(function (panel) {
			var wasHidden = panel.hasAttribute("hidden");
			var saved = panel.getAttribute("style");
			/* 絶対配置＋不可視で、表示中パネルを乱さずに計測 */
			panel.removeAttribute("hidden");
			panel.style.position = "absolute";
			panel.style.visibility = "hidden";
			panel.style.opacity = "0";
			/* 高さ: 実幅に固定して計測 */
			if (width) panel.style.width = width + "px";
			maxH = Math.max(maxH, Math.ceil(panel.getBoundingClientRect().height));
			/* 幅: 自然幅（折り返しなし）を計測 */
			panel.style.width = "max-content";
			maxW = Math.max(maxW, Math.ceil(panel.getBoundingClientRect().width));
			if (saved === null) panel.removeAttribute("style");
			else panel.setAttribute("style", saved);
			if (wasHidden) panel.setAttribute("hidden", "");
		});
		if (maxW > 340) maxW = 340;
		return maxH ? { w: maxW, h: maxH } : null;
	}

	function applyTo(area) {
		if (!area || !cache) return;
		if (cache.w) area.style.minWidth = cache.w + "px";
		if (cache.h) {
			area.style.minHeight = cache.h + "px";
			/* 各パネル自身の高さを最大パネルに固定（切替で高さが変わらない） */
			area.querySelectorAll(".dyncat-mega__panel").forEach(function (p) {
				p.style.minHeight = cache.h + "px";
			});
		}
	}

	function handle() {
		var area = tier2.querySelector(".dyncat-mega__children-area");
		if (!area) return;
		/* 開くたびに計測し、最大値を累積更新（初回計測が不完全でも自己補正） */
		var m = measureMax(area);
		if (m) {
			if (!cache) cache = m;
			else {
				cache.w = Math.max(cache.w, m.w);
				cache.h = Math.max(cache.h, m.h);
			}
			applyTo(
				document.querySelector(
					".dyncat-li .dyncat-mega .dyncat-mega__children-area",
				),
			);
		}
		applyTo(area);
	}

	new MutationObserver(handle).observe(tier2, {
		childList: true,
		subtree: true,
	});
})();

/* ============================================================
   動的2階層カテゴリメニュー — data-sort-order 並び替えフォールバック
   Liquid の sort: 'metafields.custom.sort_order' が効かないケースに備え、
   各リストの直接の子要素を data-sort-order の昇順で DOM 並び替えする。
   PageHeader.openChildMenu() の cloneNode より前に実行することで、
   clone 後の DOM も並び順を引き継ぐ。
   ============================================================ */
(function () {
	function sortByDataOrder(container) {
		if (!container) return;
		var items = Array.prototype.slice.call(container.children);
		if (items.length < 2) return;
		items.sort(function (a, b) {
			var aOrder = parseFloat(a.getAttribute("data-sort-order"));
			var bOrder = parseFloat(b.getAttribute("data-sort-order"));
			if (isNaN(aOrder)) aOrder = Infinity;
			if (isNaN(bOrder)) bOrder = Infinity;
			return aOrder - bOrder;
		});
		items.forEach(function (item) {
			container.appendChild(item);
		});
	}

	function sortSectionsByMinChildOrder(container) {
		if (!container) return;
		var sections = Array.prototype.slice.call(container.children);
		if (sections.length < 2) return;
		sections.sort(function (a, b) {
			return getMinDescendantSortOrder(a) - getMinDescendantSortOrder(b);
		});
		sections.forEach(function (sec) {
			container.appendChild(sec);
		});
	}

	function getMinDescendantSortOrder(root) {
		var min = Infinity;
		root.querySelectorAll("[data-sort-order]").forEach(function (el) {
			var n = parseFloat(el.getAttribute("data-sort-order"));
			if (!isNaN(n) && n < min) min = n;
		});
		return min;
	}

	function applySorting() {
		/* デスクトップ: 各セクション内の親リスト / 右ペインのパネル群 / 各パネルの子リスト */
		document
			.querySelectorAll(".dyncat-mega__section-list")
			.forEach(sortByDataOrder);
		document
			.querySelectorAll(".dyncat-mega__children-area")
			.forEach(sortByDataOrder);
		document
			.querySelectorAll(".dyncat-mega__children")
			.forEach(sortByDataOrder);
		/* デスクトップ: セクション自体の順序（セクション内の最小 sort_order を見て並べ替え） */
		document
			.querySelectorAll(".dyncat-mega__parents")
			.forEach(sortSectionsByMinChildOrder);

		/* モバイル: All Products パネル内の各セクション <ul> / 各サブパネルの子リスト */
		document
			.querySelectorAll(
				"#dyncat-mobile-panel .main-menu-panel__section > .main-menu-links",
			)
			.forEach(sortByDataOrder);
		document
			.querySelectorAll('[id^="dyncat-mobile-panel-"] > .main-menu-links')
			.forEach(sortByDataOrder);
		/* モバイル: セクション自体の順序 */
		document
			.querySelectorAll("#dyncat-mobile-panel")
			.forEach(sortSectionsByMinChildOrder);

		/* SP ヘッダーバー: カテゴリパネルの親リスト / 子リスト / セクション順 */
		document.querySelectorAll(".cc-dyncat-bar__list").forEach(sortByDataOrder);
		document.querySelectorAll(".cc-dyncat-bar__sub").forEach(sortByDataOrder);
		document.querySelectorAll(".cc-dyncat-bar").forEach(sortSectionsByMinChildOrder);

		/* ソート後の DOM 順に合わせて is-active を付け直す（Liquid 時点では parent_idx==1
       に固定しているが、DOM 並べ替え後は先頭要素が変わるため再同期が必要） */
		reactivateFirstParent();
	}

	function reactivateFirstParent() {
		document.querySelectorAll(".dyncat-mega").forEach(function (mega) {
			/* セクション分け構造になったため、descendant selector で全親をフラットに取得し
         document order の最初の「子あり親」を活性化する。
         子なし親（data-dyncat-target を持たない）はスキップ。 */
			var parents = mega.querySelectorAll(
				".dyncat-mega__parents .dyncat-mega__parent-item",
			);
			if (!parents.length) return;

			/* 一度全て非アクティブに */
			parents.forEach(function (item) {
				item.classList.remove("is-active");
				var link = item.querySelector(".dyncat-mega__parent-link");
				if (link && link.hasAttribute("aria-selected")) {
					link.setAttribute("aria-selected", "false");
				}
			});

			/* 最初の子あり親を見つけて activate */
			var firstHandle = null;
			for (var i = 0; i < parents.length; i++) {
				var link = parents[i].querySelector(".dyncat-mega__parent-link");
				var handle = link && link.getAttribute("data-dyncat-target");
				if (handle) {
					parents[i].classList.add("is-active");
					link.setAttribute("aria-selected", "true");
					firstHandle = handle;
					break;
				}
			}

			/* 親の先頭 handle と一致するパネルだけ is-active + 表示、他は hidden */
			var panels = mega.querySelectorAll(
				".dyncat-mega__children-area > .dyncat-mega__panel",
			);
			panels.forEach(function (panel) {
				var match =
					firstHandle &&
					panel.getAttribute("data-dyncat-panel") === firstHandle;
				panel.classList.toggle("is-active", match);
				if (match) {
					panel.removeAttribute("hidden");
				} else {
					panel.setAttribute("hidden", "");
				}
			});

			/* 初期アクティブ親のセクション右隣へパネルを配置（clone 前に実行し順序を継承） */
			ccDyncatPositionPanel(mega);
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", applySorting);
	} else {
		applySorting();
	}
})();

/* ============================================================
   メガメニュー PICKUP（mega-pickup セクション）注入 + カルーセル
   - Header Group の mega-pickup セクションの中身を複製し、
       PC : All Products メガメニュー(.dyncat-mega)の .dyncat-mega__main 下
       SP : モバイルメニューの .main-menu-links 直下
     の2箇所へ差し込む。
   - .dyncat-mega は開くたび #mega-menu-tier-2 に clone されるため、
     clone 前の元 DOM(.dyncat-li 配下)へ入れておけば clone にも含まれる。
   - カルーセルは表示中(IntersectionObserver)のものだけ自動再生する。
   ============================================================ */
(function () {
	/* ---- カルーセル ---- */
	function trackOf(carousel) {
		return carousel.querySelector("[data-pickup-track]");
	}
	function stepWidth(track) {
		var slide = track.querySelector(".mega-pickup__slide");
		if (!slide) return track.clientWidth;
		var styles = getComputedStyle(track);
		var gap = parseFloat(styles.columnGap || styles.gap) || 0;
		return slide.getBoundingClientRect().width + gap;
	}
	/* ループ可否はセクション設定（data-pickup-loop）で切り替え */
	function isLoop(track) {
		var carousel = track.closest("[data-pickup-carousel]");
		return !carousel || carousel.getAttribute("data-pickup-loop") !== "false";
	}
	function atEnd(track) {
		return track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
	}
	function advance(track, dir) {
		var loop = isLoop(track);
		if (dir > 0 && atEnd(track)) {
			if (loop) track.scrollTo({ left: 0, behavior: "smooth" });
		} else if (dir < 0 && track.scrollLeft <= 1) {
			if (loop) track.scrollTo({ left: track.scrollWidth, behavior: "smooth" });
		} else {
			track.scrollBy({ left: dir * stepWidth(track), behavior: "smooth" });
		}
	}
	function stopAuto(carousel) {
		if (carousel && carousel.__ccTimer) {
			clearInterval(carousel.__ccTimer);
			carousel.__ccTimer = null;
		}
	}
	function startAuto(carousel) {
		if (!carousel || carousel.__ccTimer) return;
		var seconds = parseFloat(carousel.getAttribute("data-autoplay")) || 0;
		var track = trackOf(carousel);
		if (!seconds || !track) return;
		carousel.__ccTimer = setInterval(function () {
			/* ループオフのときは末尾で自動再生を止める */
			if (!isLoop(track) && atEnd(track)) {
				stopAuto(carousel);
				return;
			}
			advance(track, 1);
		}, seconds * 1000);
	}
	/* 各カルーセルは可視のときだけ再生（PC メガ開閉・SP ドロワー開閉に追従） */
	function setupAutoplay(carousel) {
		if (!carousel || carousel.__ccSetup) return;
		carousel.__ccSetup = true;
		carousel.addEventListener("mouseenter", function () {
			stopAuto(carousel);
		});
		carousel.addEventListener("mouseleave", function () {
			if (carousel.offsetParent) startAuto(carousel);
		});
		if ("IntersectionObserver" in window) {
			new IntersectionObserver(function (entries) {
				entries.forEach(function (en) {
					if (en.isIntersecting) startAuto(carousel);
					else stopAuto(carousel);
				});
			}, { threshold: 0.2 }).observe(carousel);
		} else {
			startAuto(carousel);
		}
	}

	/* 矢印は document 委譲（clone 後の要素にも対応） */
	document.addEventListener("click", function (e) {
		var btn = e.target.closest("[data-pickup-prev], [data-pickup-next]");
		if (!btn) return;
		var carousel = btn.closest("[data-pickup-carousel]");
		if (!carousel) return;
		e.preventDefault();
		e.stopPropagation();
		var track = trackOf(carousel);
		if (track) advance(track, btn.hasAttribute("data-pickup-next") ? 1 : -1);
	});

	/* ---- 注入 ---- */
	function buildContent() {
		var src = document.querySelector(
			"[data-mega-pickup] .mega-pickup__inner",
		);
		return src ? src.cloneNode(true) : null;
	}
	function injectInto(parent, wrapClass, refNode) {
		if (!parent) return;
		var existing = parent.querySelector(":scope > ." + wrapClass);
		if (existing) existing.remove();
		var content = buildContent();
		if (!content) return;
		var wrap = document.createElement("div");
		wrap.className = wrapClass;
		wrap.appendChild(content);
		if (refNode) refNode.insertAdjacentElement("afterend", wrap);
		else parent.appendChild(wrap);
		setupAutoplay(wrap.querySelector("[data-pickup-carousel]"));
	}
	function injectPickup() {
		/* PC: All Products メガメニュー右端（clone 元へ） */
		var mega = document.querySelector(".dyncat-li .dyncat-mega");
		if (mega) injectInto(mega, "dyncat-mega__pickup");
		/* SP: main-menu-links の直下 */
		var links = document.querySelector("#main-menu-panel > .main-menu-links");
		if (links)
			injectInto(links.parentNode, "mega-pickup__mobile", links);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", injectPickup);
	} else {
		injectPickup();
	}
	/* テーマエディタでの再描画に追従 */
	document.addEventListener("shopify:section:load", injectPickup);
	document.addEventListener("shopify:section:unload", injectPickup);

	/* PC: 開いたメガメニュー(#mega-menu-tier-2)内の clone カルーセルを再生対象に */
	var tier2 = document.getElementById("mega-menu-tier-2");
	if (tier2 && "MutationObserver" in window) {
		new MutationObserver(function () {
			setupAutoplay(tier2.querySelector("[data-pickup-carousel]"));
		}).observe(tier2, { childList: true, subtree: true });
	}
})();

/* =================================================================================================
 * 商品ページ：アコーディオン (.product-acodion__area)
 * =================================================================================================
 * HTML は商品詳細のリッチテキストに埋め込み済み。
 *  - 各 .nav-open クリックで直後の .nav-open__list を開閉
 *  - 初期表示では先頭の .product-acodion__area をアクティブ（開いた状態）にする
 * 開閉の見た目は custom.css の .product-acodion__area / .is-active 側で制御する。
 * ================================================================================================= */
(function () {
	// 1 つの .product-acodion__area を初期化する（二重初期化は dataset で防止）
	function setupArea(area, shouldOpen) {
		if (area.dataset.accordionInit === "1") return;
		var trigger = area.querySelector(".nav-open");
		if (!trigger) return;
		area.dataset.accordionInit = "1";

		/* grid の 0fr→1fr で全行を畳めるよう、リストの中身を単一ラッパーに包む。
		 * リッチテキストのリストは直接の子が複数あり、そのままだと2行目以降が畳まれない。 */
		var list = area.querySelector(".nav-open__list");
		if (list && !list.querySelector(":scope > .nav-open__inner-wrap")) {
			var wrap = document.createElement("div");
			wrap.className = "nav-open__inner-wrap";
			while (list.firstChild) wrap.appendChild(list.firstChild);
			list.appendChild(wrap);
		}

		area.classList.toggle("is-active", shouldOpen);
		trigger.setAttribute("role", "button");
		trigger.setAttribute("tabindex", "0");
		trigger.setAttribute("aria-expanded", shouldOpen ? "true" : "false");

		trigger.addEventListener("click", function () {
			var isActive = area.classList.toggle("is-active");
			trigger.setAttribute("aria-expanded", isActive ? "true" : "false");
		});
		trigger.addEventListener("keydown", function (event) {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				trigger.click();
			}
		});
	}

	// root 配下を初期化。openStates 指定時はその開閉状態を復元、無指定は先頭のみ開く
	function initAccordion(root, openStates) {
		var areas = (root || document).querySelectorAll(".product-acodion__area");
		areas.forEach(function (area, index) {
			var shouldOpen = openStates ? !!openStates[index] : index === 0;
			setupArea(area, shouldOpen);
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			initAccordion();
		});
	} else {
		initAccordion();
	}

	// variant 切替の DOM 置換：挿入前の新ノードに開閉状態を引き継いで初期化しちらつきを防ぐ
	document.addEventListener("on:variant:before-replace-element", function (event) {
		var detail = event.detail || {};
		var replaceWith = detail.replaceWith;
		if (!replaceWith || typeof replaceWith.querySelector !== "function") return;
		if (!replaceWith.querySelector(".product-acodion__area")) return;

		var oldAreas = detail.toReplace
			? detail.toReplace.querySelectorAll(".product-acodion__area")
			: [];
		var openStates = Array.prototype.map.call(oldAreas, function (a) {
			return a.classList.contains("is-active");
		});
		initAccordion(replaceWith, oldAreas.length ? openStates : null);
	});

	// フォールバック：before-replace を経由しなかった置換分の未初期化アコーディオンを初期化
	document.addEventListener("on:variant:change", function () {
		initAccordion();
	});
})();

/* ============================================================
   SP / タブレット ヘッダーバーの子項目パネル開閉
   - 子項目を持つ項目 (.cc-header-bar__toggle) を押すと、
     バーの下にパネルが上から下に開く。
   - 同時に開くのは1つ。バー外クリック / Escape で閉じる。
   ============================================================ */
(function () {
	"use strict";

	var bar = document.querySelector(".cc-header-bar");
	if (!bar) return;

	function closeAll() {
		bar
			.querySelectorAll('.cc-header-bar__toggle[aria-expanded="true"]')
			.forEach(function (btn) {
				btn.setAttribute("aria-expanded", "false");
			});
		bar.querySelectorAll(".cc-header-bar__panel.is-open").forEach(function (p) {
			p.classList.remove("is-open");
		});
		closeAllSubs();
	}

	// CATEGORY パネル内の親アコーディオンを全て閉じる
	function closeAllSubs() {
		bar
			.querySelectorAll('.cc-dyncat-bar__parent--toggle[aria-expanded="true"]')
			.forEach(function (btn) {
				btn.setAttribute("aria-expanded", "false");
			});
		bar
			.querySelectorAll(".cc-dyncat-bar__sub-wrap.is-open")
			.forEach(function (wrap) {
				wrap.classList.remove("is-open");
			});
	}

	bar.addEventListener("click", function (e) {
		// CATEGORY パネル内: 親カテゴリのアコーディオン開閉 (同時に開くのは1つ)
		var subBtn = e.target.closest(".cc-dyncat-bar__parent--toggle");
		if (subBtn) {
			var subOpen = subBtn.getAttribute("aria-expanded") === "true";
			var sub = document.getElementById(subBtn.getAttribute("aria-controls"));
			closeAllSubs();
			if (subOpen || !sub) return;
			subBtn.setAttribute("aria-expanded", "true");
			sub.classList.add("is-open");
			return;
		}

		var btn = e.target.closest(".cc-header-bar__toggle");
		if (!btn) return;

		var panel = document.getElementById(btn.getAttribute("aria-controls"));
		var isOpen = btn.getAttribute("aria-expanded") === "true";
		closeAll();
		if (isOpen || !panel) return;

		btn.setAttribute("aria-expanded", "true");
		panel.classList.add("is-open");
	});

	document.addEventListener("click", function (e) {
		if (!e.target.closest(".cc-header-bar")) closeAll();
	});

	document.addEventListener("keydown", function (e) {
		if (e.key === "Escape") closeAll();
	});
})();

/* 検索クエリの整形: 説明文を検索対象から外し、対象は商品のみ */
(function () {
	// body を除いた検索対象。variants.title はフィールド指定が効かないため含めない
	var SEARCH_FIELDS = ["title", "product_type", "tag", "vendor", "variants.sku"];

	// 色はオプション値にしか無くフィールド指定では拾えないため素のまま送る
	var COLOR_WORDS = [
		"ブラック", "ホワイト", "ブラウン", "ベージュ", "グレー", "ブルー", "グリーン",
		"ピンク", "レッド", "イエロー", "オレンジ", "パープル", "ネイビー", "ボルドー",
		"キャメル", "トープ", "カーキ", "シルバー", "ゴールド", "ブロンズ", "モカ",
		"ラベンダー", "アイボリー", "マスタード", "テラコッタ", "レオパード", "ゼブラ",
		"レモン", "ペパーミント", "ストライプ", "チドリ", "ドット", "フラワー", "ひし形"
	];

	// 利用者が自分でフィールド指定を書いた場合はそのまま通す
	var FIELD_SYNTAX = /(^|\s)(title|body|product_type|tag|vendor|variants\.[a-z]+):/;

	function isColorTerm(word) {
		return COLOR_WORDS.some(function (w) {
			return word.indexOf(w) !== -1;
		});
	}

	// 各語を body 以外のフィールド指定の OR に展開し、語どうしは AND で結ぶ
	function buildQuery(term) {
		if (FIELD_SYNTAX.test(term)) return term;

		var words = term.split(/\s+/).filter(Boolean);
		if (words.some(isColorTerm)) return term;

		return words.map(function (word) {
			return "(" + SEARCH_FIELDS.map(function (field) {
				return field + ":" + word;
			}).join(" OR ") + ")";
		}).join(" AND ");
	}

	// 展開したクエリから元の検索語を取り出す
	function plainTerm(query) {
		var words = [];
		var re = /\btitle:([^\s)]+)/g;
		var m;

		while ((m = re.exec(query)) !== null) words.push(m[1]);
		return words.join(" ");
	}

	// 検索窓に展開後のクエリが残らないよう、送信を止めて URL を組み立てて遷移する
	document.addEventListener("submit", function (e) {
		var form = e.target;
		if (!form || !form.getAttribute) return;

		var action = form.getAttribute("action") || "";
		if (!/\/search\b/.test(action)) return;

		var input = form.querySelector('input[name="q"]');
		if (!input) return;

		e.preventDefault();
		e.stopPropagation();

		var term = input.value.trim();

		// 空検索は結果が0件になるため全商品一覧へ送る
		if (!term) {
			var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
			window.location.href = root.replace(/\/$/, "") + "/collections/all";
			return;
		}

		var params = new URLSearchParams(new FormData(form));
		params.set("q", buildQuery(term));
		params.set("type", "product");
		window.location.href = action + "?" + params.toString();
	}, true);

	// 検索ページでは展開したクエリを元の語に戻して表示する
	function restoreTerm() {
		if (!/^\/search/.test(window.location.pathname)) return;

		var query = new URLSearchParams(window.location.search).get("q") || "";
		if (query.indexOf("title:") === -1) return;

		var plain = plainTerm(query);
		if (!plain) return;

		document.querySelectorAll('form.search input[name="q"]').forEach(function (input) {
			input.value = plain;
			input.setAttribute("value", plain);
		});

		document.title = document.title.split(query).join(plain);

		document.querySelectorAll('[role="status"]').forEach(function (el) {
			if (el.textContent.indexOf(query) === -1) return;
			el.textContent = el.textContent.split(query).join(plain);
		});
	}

	restoreTerm();
	document.documentElement.classList.add("cc-search-ready");
	window.addEventListener("pageshow", restoreTerm);
})();
